import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ShieldCheckIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
import { privacyRequestTypes, usStates } from '../data/statePrivacyLaws';
const inputClass = 'block w-full px-4 py-3 text-base border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-navy';

/** Browsers/extensions that send Global Privacy Control expose this flag. */
function hasGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & {
    globalPrivacyControl?: boolean;
  }).globalPrivacyControl === true;
}
export function DoNotSellMyInfoPage() {
  usePageMeta('Do Not Sell or Share My Personal Information | FindOnlineColleges', 'Opt out of the sale or sharing of your personal information, or submit a request to access, correct, delete, or port your data. Available to all U.S. consumers.');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [gpcActive] = useState(hasGpcSignal);
  const [formData, setFormData] = useState({
    requestType: 'opt-out',
    fullName: '',
    email: '',
    phone: '',
    state: '',
    isAgent: false,
    note: ''
  });
  const selectedType = privacyRequestTypes.find(t => t.value === formData.requestType);
  const isOptOut = formData.requestType === 'opt-out';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const stateName = usStates.find(s => s.code === formData.state)?.name ?? formData.state;
    const subject = `Privacy Request: ${selectedType?.label ?? formData.requestType} (${formData.state})`;
    const body = ['A new consumer privacy request has been submitted:', '', `Request Type: ${selectedType?.label ?? formData.requestType}`, `Full Name: ${formData.fullName}`, `Email: ${formData.email}`, `Phone: ${formData.phone || 'N/A'}`, `State of Residence: ${stateName}`, `Submitted by Authorized Agent: ${formData.isAgent ? 'Yes' : 'No'}`, `Global Privacy Control Detected: ${gpcActive ? 'Yes' : 'No'}`, `Additional Details: ${formData.note || 'N/A'}`, `Submitted: ${new Date().toISOString()}`].join('\n');
    window.location.href = `mailto:admin@media-matchers.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setIsSubmitted(true);
  };
  return <main className="bg-white py-10 sm:py-16" data-id="element-1511" data-mp="1luqduwhw4d6l">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1512" data-mp="1pypl0oha5zna">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1513" data-mp="1bywyl5cse5l4">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1514" data-mp="1gzuhxl9j95o7" />
          Back to home
        </Link>

        <header className="border-b border-gray-200 pb-8 mb-10" data-id="element-1515" data-mp="i6p9dd2gxna">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1516" data-mp="5iuo2l7jeis2">
            Do Not Sell or Share My Personal Information
          </h1>
          <p className="text-lg text-gray-600 mt-4 leading-relaxed" data-id="element-1517" data-mp="3p5d23jlfbim4">
            Opt out, or make any other privacy request. Available to every U.S.
            consumer, wherever you live.
          </p>
        </header>

        {gpcActive && <div role="status" className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl p-5 mb-10" data-id="element-1518" data-mp="21ylljpiko1d">
            <ShieldCheckIcon className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-1519" data-mp="36f57xqzpc2i2" />
            <p className="text-sm text-green-900 leading-relaxed" data-id="element-1520" data-mp="3k7uubwj579fp">
              <strong data-id="element-1521" data-mp="3ffbfyxcmsa9u">Global Privacy Control detected.</strong> Your browser is
              already opted out of sale, sharing, and targeted advertising on
              this site. To apply the opt-out to your contact record as well,
              submit the form below.
            </p>
          </div>}

        <div className="space-y-5 leading-relaxed text-base mb-12" data-id="element-1522" data-mp="30eo1i45su04g">
          <p data-id="element-1523" data-mp="35cg4pubw98ny">
            We do not sell your personal information for money. However, when we
            share information with educational partners and advertising
            partners, this may count as a "sale," "sharing," or "targeted
            advertising" under California, Colorado, Connecticut, Virginia,
            Texas, Oregon, and the other state privacy laws listed in our{' '}
            <Link to="/privacy-policy#section-11" className="text-navy underline hover:text-crimson" data-id="element-1524" data-mp="3o2xj0tvzc1ps">
              Privacy Policy
            </Link>
            . You can opt out of all of these at once below. You do not need to
            verify your identity to opt out.
          </p>
          <p data-id="element-1525" data-mp="1ypaww4cz7cu2">
            You can also use this form to access, port, correct, or delete your
            information, get a list of third parties, stop marketing, or appeal
            a decision. Prefer another method? Email{' '}
            <a href="mailto:contact@findonlinecolleges.org?subject=Privacy%20Rights%20Request" className="text-navy underline hover:text-crimson" data-id="element-1526" data-mp="ktj02ppj0c24">
              contact@findonlinecolleges.org
            </a>{' '}
            with the subject "Privacy Rights Request," write to Birddog Media,
            Attn: Privacy Requests, 1226 W. Northwest Highway, Palatine, IL
            60067, or turn on Global Privacy Control in your browser.
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-10" data-id="element-1527" data-mp="zne2pdz3954v">
          {isSubmitted ? <div className="text-center py-8" role="status" data-id="element-1528" data-mp="us8g0cxv3mfq">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6" data-id="element-1529" data-mp="pqsxosn2ju11">
                <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-1530" data-mp="3fhxxkxpy9mc4" />
              </div>
              <h2 className="text-2xl font-bold text-navy mb-4" data-id="element-1531" data-mp="3afyu7nu7mpf6">
                Request Received
              </h2>
              <p className="text-gray-600 max-w-md mx-auto" data-id="element-1532" data-mp="3bwmb20nqrxna">
                {isOptOut ? 'Thank you. We will process your opt-out as soon as possible, and no later than 15 business days.' : 'Thank you. We will confirm receipt within 10 business days, and respond within 45 days. We may contact you at the email address you gave us to verify your identity.'}
              </p>
            </div> : <>
              <h2 className="text-2xl font-bold text-navy mb-6" data-id="element-1533" data-mp="2xzzcxuk1z1fs">
                Privacy Request Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1534" data-mp="2k940676fo06x">
                <fieldset data-id="element-1535" data-mp="2o8csr8pqls0w">
                  <legend className="block text-sm font-bold text-gray-800 mb-3" data-id="element-1536" data-mp="2o9nwj8j6mzs0">
                    What would you like to do?{' '}
                    <span className="text-crimson" data-id="element-1537" data-mp="2f8gcnvhey1hg">*</span>
                  </legend>
                  <div className="space-y-2" data-id="element-1538" data-mp="2faj48jy7s2oa">
                    {privacyRequestTypes.map(type => <label key={type.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors duration-150 ${formData.requestType === type.value ? 'border-navy bg-white' : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-1539" data-mp="1vohfxrtj68co">
                        <input type="radio" name="requestType" value={type.value} checked={formData.requestType === type.value} onChange={e => setFormData({
                    ...formData,
                    requestType: e.target.value
                  })} className="mt-1 h-4 w-4 text-navy focus:ring-navy" data-id="element-1540" data-mp="29le8bxparxvo" />
                        <span className="text-sm text-gray-800 leading-snug" data-id="element-1541" data-mp="8ijub2bmt6az">
                          {type.label}
                        </span>
                      </label>)}
                  </div>
                </fieldset>

                <div data-id="element-1542" data-mp="hiyhk785ovh4">
                  <label htmlFor="fullName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1543" data-mp="3gyv6abkswryw">
                    Full Name <span className="text-crimson" data-id="element-1544" data-mp="3ly7t4wbo9z1h">*</span>
                  </label>
                  <input type="text" id="fullName" required autoComplete="name" value={formData.fullName} onChange={e => setFormData({
                ...formData,
                fullName: e.target.value
              })} className={inputClass} data-id="element-1545" data-mp="3pyrrg37zf8p7" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1546" data-mp="2uod9mfvv7jzv">
                  <div data-id="element-1547" data-mp="2prwc4v2rzta4">
                    <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1548" data-mp="2ppa30r08l5ps">
                      Email Address <span className="text-crimson" data-id="element-1549" data-mp="2gq6om0c0lehm">*</span>
                    </label>
                    <input type="email" id="email" required autoComplete="email" value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className={inputClass} data-id="element-1550" data-mp="2bsdgh1r5iaep" />
                  </div>
                  <div data-id="element-1551" data-mp="1o210d3lr57d1">
                    <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1552" data-mp="1o1hjlq7u8p0l">
                      Phone Number{' '}
                      <span className="text-gray-500 font-normal" data-id="element-1553" data-mp="1xv8fq550smaw">
                        (Optional)
                      </span>
                    </label>
                    <input type="tel" id="phone" autoComplete="tel" value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} aria-describedby="phone-help" className={inputClass} data-id="element-1554" data-mp="2bnobckvldi1s" />
                    <p id="phone-help" className="text-xs text-gray-500 mt-1.5" data-id="element-1555" data-mp="1nvzqfpl3nvuv">
                      Helps us find records tied to calls or texts.
                    </p>
                  </div>
                </div>

                <div data-id="element-1556" data-mp="2zampvd05ifx2">
                  <label htmlFor="state" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1557" data-mp="2zb6aj1h6ries">
                    State of Residence <span className="text-crimson" data-id="element-1558" data-mp="q3j9b2con5y8">*</span>
                  </label>
                  <select id="state" required value={formData.state} onChange={e => setFormData({
                ...formData,
                state: e.target.value
              })} className={inputClass} data-id="element-1559" data-mp="c3qkjckpuzsw">
                    <option value="" data-id="element-1560" data-mp="18spztbjpx1ul">Select a state</option>
                    {usStates.map(s => <option key={s.code} value={s.code} data-id="element-1561" data-mp="v1ktxrprbdbt">
                        {s.name}
                      </option>)}
                  </select>
                </div>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-1562" data-mp="1noxiu8je3x8a">
                  <input type="checkbox" checked={formData.isAgent} onChange={e => setFormData({
                ...formData,
                isAgent: e.target.checked
              })} className="mt-1 h-4 w-4 rounded text-navy focus:ring-navy" data-id="element-1563" data-mp="1sia3k7ky7jvr" />
                  <span className="text-sm text-gray-700 leading-snug" data-id="element-1564" data-mp="1sh478k5wbcku">
                    I am an authorized agent submitting this request for
                    someone else. We will ask you for their signed permission or
                    a power of attorney.
                  </span>
                </label>

                <div data-id="element-1565" data-mp="19ojjti8ytzof">
                  <label htmlFor="note" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-1566" data-mp="19o08ikdtm9jd">
                    {formData.requestType === 'correct' ? 'What should be corrected?' : formData.requestType === 'appeal' ? 'Which decision are you appealing, and why?' : 'Additional Details'}{' '}
                    {formData.requestType !== 'correct' && formData.requestType !== 'appeal' && <span className="text-gray-500 font-normal" data-id="element-1567" data-mp="3gcsktvf75bxp">
                          (Optional)
                        </span>}
                    {(formData.requestType === 'correct' || formData.requestType === 'appeal') && <span className="text-crimson" data-id="element-1568" data-mp="2nm1v4asjsqvw">*</span>}
                  </label>
                  <textarea id="note" rows={4} required={formData.requestType === 'correct' || formData.requestType === 'appeal'} value={formData.note} onChange={e => setFormData({
                ...formData,
                note: e.target.value
              })} className={`${inputClass} resize-none`} data-id="element-1569" data-mp="2skv7u100gdv0" />
                </div>

                <button type="submit" className="w-full bg-cta hover:bg-cta-dark text-white font-bold py-4 px-6 rounded-lg text-base transition-colors duration-200 shadow-sm" data-id="element-1570" data-mp="bfg249x11i69">
                  {isOptOut ? 'Submit Opt-Out Request' : 'Submit Privacy Request'}
                </button>
                <p className="text-xs text-gray-500 text-center" data-id="element-1571" data-mp="z2xm3ja1vbyo">
                  We only use this information to process your request. We will
                  not discriminate against you for exercising your rights.
                </p>
              </form>
            </>}
        </div>
      </div>
    </main>;
}